<template>
  <div class="kds-protocol-loop">
    <!-- Decorative: the caption row below carries the meaning in text. -->
    <canvas ref="canvasRef" class="kds-protocol-loop__canvas" aria-hidden="true" />

    <p class="kds-protocol-loop__pill font-mono">
      <span class="kds-protocol-loop__dot" aria-hidden="true" />
      {{ t('landing.protocols.loop.turn') }}
      <b class="kds-protocol-loop__turn">{{ turn }}</b>
      / {{ turns }}
    </p>

    <div class="kds-protocol-loop__footer">
      <p class="kds-protocol-loop__caption font-mono" aria-live="polite">{{ caption }}</p>
      <p
        class="kds-protocol-loop__verdict font-mono"
        :class="{ 'kds-protocol-loop__verdict--on': verdictOn }"
      >
        {{ verdictLabel }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import type { ProtocolKind, ProtocolLoopHandle } from '@/components/protocols/protocolLoop';

const props = defineProps<{
  kind: ProtocolKind;
  /** Total number of turns, shown as the pill denominator. */
  turns: number;
  /** Verdict caption, e.g. "VERDICT · D-141". */
  verdictLabel: string;
}>();

const { t } = useI18n();

/** One full lap, in seconds. The loop closes on itself. Fixed by the animation's easing curves. */
const LOOP_SECONDS = 10;

/**
 * The animation reports its step as a canonical English string. Mapping it here
 * rather than indexing by turn is deliberate: P-02 has four steps across three
 * turns (a closing synthesis follows the last one), so the turn index alone
 * cannot name the step.
 */
const STEP_KEYS: Record<string, string> = {
  'CONSENSUS STATED': 'consensusStated',
  'ADVOCATE CONTRADICTS': 'advocateContradicts',
  'PANEL SPLITS': 'panelSplits',
  'ASSUMPTION SURFACED': 'assumptionSurfaced',
  'FIRST CRITIQUE PASS': 'firstCritiquePass',
  'REBUTTAL ROUND': 'rebuttalRound',
  'STRESS-TEST ROUND': 'stressTestRound',
  'CLOSING SYNTHESIS': 'closingSynthesis',
  'DECISION FRAMED': 'decisionFramed',
  'JOBS PROJECTED': 'jobsProjected',
  'TRADE-OFFS WEIGHED': 'tradeOffsWeighed',
  'ROADMAP ORDERED': 'roadmapOrdered'
};

const canvasRef = ref<HTMLCanvasElement | null>(null);
const turn = ref(1);
const rawStep = ref('');
const verdictOn = ref(false);

const caption = computed(() => {
  const key = STEP_KEYS[rawStep.value];
  // Unmapped step: show the raw label rather than an empty caption.
  return key ? t(`landing.protocols.steps.${key}`) : rawStep.value;
});

let handle: ProtocolLoopHandle | null = null;
let frame: number | null = null;
let observer: IntersectionObserver | null = null;
let startedAt: number | null = null;
let destroyed = false;

const applyState = (p: number) => {
  if (!handle) return;
  handle.render(p);
  const state = handle.read(p);
  if (state.turn !== turn.value) turn.value = state.turn;
  if (state.step !== rawStep.value) rawStep.value = state.step;
  if (state.verdict !== verdictOn.value) verdictOn.value = state.verdict;
};

const stop = () => {
  if (frame !== null) {
    cancelAnimationFrame(frame);
    frame = null;
  }
};

const tick = (now: number) => {
  if (destroyed || !handle) return;
  if (startedAt === null) startedAt = now;
  applyState(((now - startedAt) / 1000 / LOOP_SECONDS) % 1);
  frame = requestAnimationFrame(tick);
};

const start = () => {
  if (destroyed || frame !== null) return;
  frame = requestAnimationFrame(tick);
};

/**
 * three.js is loaded here and nowhere else, so it never reaches the initial
 * bundle. The import only fires once the card is actually near the viewport.
 */
const boot = async () => {
  const canvas = canvasRef.value;
  if (destroyed || handle || !canvas) return;

  try {
    const { mount } = await import('@/components/protocols/protocolLoop');
    if (destroyed || !canvasRef.value) return;

    // Signal green comes from the stylesheet, not from a literal in the shader.
    const accent = getComputedStyle(document.documentElement)
      .getPropertyValue('--signal')
      .trim();
    handle = mount(canvas, props.kind, accent ? { accent } : {});

    if (prefersReducedMotion()) {
      // One representative frame, held: the panel has split and the verdict is
      // about to land. No animation loop is ever started.
      applyState(0.72);
      return;
    }
    start();
  } catch (err) {
    // No WebGL context, a blocked chunk, a headless renderer: the card keeps its
    // heading, description and best-for line, and simply shows no animation.
    // A decorative canvas must never take the section down with it.
    console.warn('[KdsProtocolLoop] animation unavailable', err);
    handle = null;
  }
};

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

onMounted(() => {
  const canvas = canvasRef.value;
  if (!canvas) return;

  // WebGL contexts are a scarce resource (browsers cap them around 16), so a
  // loop is only built once its card approaches the viewport, and it stops
  // drawing as soon as it leaves.
  if (typeof IntersectionObserver !== 'function') {
    void boot();
    return;
  }

  observer = new IntersectionObserver(
    (entries) => {
      const visible = entries.some((e) => e.isIntersecting);
      if (visible) {
        if (handle) {
          if (!prefersReducedMotion()) start();
        } else {
          void boot();
        }
      } else {
        stop();
      }
    },
    { rootMargin: '120px' }
  );
  observer.observe(canvas);
});

onBeforeUnmount(() => {
  destroyed = true;
  stop();
  observer?.disconnect();
  observer = null;
  handle?.dispose();
  handle = null;
});
</script>

<style scoped lang="scss">
.kds-protocol-loop {
  position: relative;
  aspect-ratio: 1 / 1;
  width: 100%;

  &__canvas {
    display: block;
    width: 100%;
    height: 100%;
  }

  &__pill {
    position: absolute;
    top: var(--space-1);
    right: 0;
    display: flex;
    align-items: center;
    gap: var(--space-2);
    margin: 0;
    padding: var(--space-1) var(--space-3);
    border: var(--border);
    border-radius: var(--r-pill);
    background: var(--paper);
    font-size: var(--fs-mono);
    letter-spacing: 0.12em;
    color: var(--stone);
    white-space: nowrap;
  }

  &__dot {
    width: 5px;
    height: 5px;
    border-radius: var(--r-pill);
    background: var(--signal);
    flex-shrink: 0;
    animation: kds-protocol-loop-blink 2.4s var(--ease-out) infinite;

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }

  &__turn {
    color: var(--ink);
    font-weight: var(--fw-medium);
  }

  &__footer {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--space-3);
  }

  &__caption {
    margin: 0;
    font-size: var(--fs-mono);
    letter-spacing: 0.12em;
    color: var(--ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__verdict {
    margin: 0;
    font-size: var(--fs-mono);
    letter-spacing: 0.1em;
    color: var(--signal);
    white-space: nowrap;
    opacity: 0;
    transition: opacity var(--d-slow) var(--ease-out);

    &--on {
      opacity: 1;
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }
}

@keyframes kds-protocol-loop-blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.35;
  }
}
</style>
